import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Ticket, Plus, Copy, Check, Trash2, Eye, Users, TrendingUp, Gift, Percent, Clock, Calendar, Pencil, Megaphone, BarChart3, Target, Mail } from 'lucide-react';
import { toast } from 'sonner';
import AdminEmailMarketing from './AdminEmailMarketing';

const API_URL = "https://api.recoverymatch.com";

const AdminMarketing = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [activeSection, setActiveSection] = useState('email'); // 'email', 'promo', or 'campaigns'
  
  // Create form state
  const [newCode, setNewCode] = useState({
    code: '',
    promo_type: 'free_months',
    value: 1,
    description: '',
    display_message: '',
    max_uses: '',
    expires_at: '',
    tier_restriction: '',
    first_time_only: true,
  });

  const fetchPromoCodes = useCallback(async () => {
    try {
      // Use admin token, not user token
      const token = localStorage.getItem('recoverymatch_admin_token') || localStorage.getItem('token');
      console.log('[AdminPromoCodes] Fetching promo codes...');
      const response = await fetch(`${API_URL}/api/admin/promo-codes?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[AdminPromoCodes] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AdminPromoCodes] API error:', errorText);
        toast.error('Failed to load promo codes');
        return;
      }
      
      const data = await response.json();
      console.log('[AdminPromoCodes] Response data:', data);
      setPromoCodes(data.promo_codes || []);
      console.log('[AdminPromoCodes] Set promoCodes:', data.promo_codes?.length || 0);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('recoverymatch_admin_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/promo-codes/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchPromoCodes();
    fetchStats();
  }, [fetchPromoCodes, fetchStats]);

  const handleCreateCode = async () => {
    try {
      const token = localStorage.getItem('recoverymatch_admin_token') || localStorage.getItem('token');
      const payload = {
        ...newCode,
        value: parseInt(newCode.value),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        expires_at: newCode.expires_at || null,
        tier_restriction: newCode.tier_restriction || null,
        code: newCode.code || null,
      };

      const response = await fetch(`${API_URL}/api/admin/promo-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Promo code ${data.promo_code.code} created!`);
        setShowCreateDialog(false);
        setNewCode({
          code: '',
          promo_type: 'free_months',
          value: 1,
          description: '',
          display_message: '',
          max_uses: '',
          expires_at: '',
          tier_restriction: '',
          first_time_only: true,
        });
        fetchPromoCodes();
        fetchStats();
      } else {
        toast.error(data.detail || 'Failed to create promo code');
      }
    } catch (error) {
      console.error('Failed to create promo code:', error);
      toast.error('Failed to create promo code');
    }
  };

  const handleDeactivate = async (codeId) => {
    if (!window.confirm('Are you sure you want to deactivate this promo code?')) return;
    
    try {
      const token = localStorage.getItem('recoverymatch_admin_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/promo-codes/${codeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Promo code deactivated');
        fetchPromoCodes();
        fetchStats();
      } else {
        toast.error('Failed to deactivate promo code');
      }
    } catch (error) {
      toast.error('Failed to deactivate promo code');
    }
  };

  const handleEditCode = (code) => {
    setEditingCode({
      id: code.id,
      code: code.code,
      promo_type: code.promo_type,
      value: code.value,
      description: code.description || '',
      display_message: code.display_message || '',
      max_uses: code.max_uses || '',
      expires_at: code.expires_at ? code.expires_at.split('T')[0] : '',
      is_active: code.is_active,
      first_time_only: code.first_time_only,
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('recoverymatch_admin_token') || localStorage.getItem('token');
      const payload = {
        description: editingCode.description,
        display_message: editingCode.display_message,
        max_uses: editingCode.max_uses ? parseInt(editingCode.max_uses) : null,
        expires_at: editingCode.expires_at || null,
        is_active: editingCode.is_active,
        first_time_only: editingCode.first_time_only,
      };

      const response = await fetch(`${API_URL}/api/admin/promo-codes/${editingCode.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Promo code ${editingCode.code} updated!`);
        setShowEditDialog(false);
        setEditingCode(null);
        fetchPromoCodes();
        fetchStats();
      } else {
        toast.error(data.detail || 'Failed to update promo code');
      }
    } catch (error) {
      console.error('Failed to update promo code:', error);
      toast.error('Failed to update promo code');
    }
  };

  const handleViewDetails = async (code) => {
    try {
      const token = localStorage.getItem('recoverymatch_admin_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/promo-codes/${code.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedCode(data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load promo code details');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied to clipboard!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getPromoTypeLabel = (type) => {
    const labels = {
      'percentage_off': 'Percentage Off',
      'free_months': 'Free Months',
      'trial_extension': 'Trial Extension',
      'lifetime_discount': 'Lifetime Discount'
    };
    return labels[type] || type;
  };

  const getPromoTypeIcon = (type) => {
    const icons = {
      'percentage_off': <Percent className="h-4 w-4" />,
      'free_months': <Gift className="h-4 w-4" />,
      'trial_extension': <Clock className="h-4 w-4" />,
      'lifetime_discount': <TrendingUp className="h-4 w-4" />
    };
    return icons[type] || <Ticket className="h-4 w-4" />;
  };

  const getValueDisplay = (code) => {
    switch (code.promo_type) {
      case 'percentage_off':
      case 'lifetime_discount':
        return `${code.value}% off`;
      case 'free_months':
        return `${code.value} month${code.value > 1 ? 's' : ''} free`;
      case 'trial_extension':
        return `+${code.value} days`;
      default:
        return code.value;
    }
  };

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    if (code.is_expired) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    if (code.is_maxed_out) {
      return <Badge variant="outline">Maxed Out</Badge>;
    }
    return <Badge className="bg-green-500">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-marketing">
      {/* Section Navigation */}
      <div className="flex gap-2 border-b pb-2">
        <Button 
          variant={activeSection === 'email' ? 'default' : 'ghost'}
          onClick={() => setActiveSection('email')}
          className="gap-2"
          data-testid="email-marketing-tab"
        >
          <Mail className="h-4 w-4" />
          Email Marketing
        </Button>
        <Button 
          variant={activeSection === 'promo' ? 'default' : 'ghost'}
          onClick={() => setActiveSection('promo')}
          className="gap-2"
        >
          <Ticket className="h-4 w-4" />
          Promo Codes
        </Button>
        <Button 
          variant={activeSection === 'campaigns' ? 'default' : 'ghost'}
          onClick={() => setActiveSection('campaigns')}
          className="gap-2"
        >
          <BarChart3 className="h-4 w-4" />
          Campaign Stats
        </Button>
      </div>

      {/* Email Marketing Section */}
      {activeSection === 'email' && (
        <AdminEmailMarketing />
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Codes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_codes}</div>
              <p className="text-xs text-muted-foreground">{stats.active_codes} active</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Redemptions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_redemptions}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Free Months Given</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.redemptions_by_type?.free_months || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Top Code</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.top_codes?.[0]?.code || '-'}</div>
              <p className="text-xs text-muted-foreground">{stats.top_codes?.[0]?.current_uses || 0} uses</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaign Stats Section */}
      {activeSection === 'campaigns' && (
        <div className="space-y-6">
          {/* Campaign Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Campaign Performance
              </CardTitle>
              <CardDescription>Track how each promo code is performing</CardDescription>
            </CardHeader>
            <CardContent>
              {promoCodes.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No campaigns yet</p>
              ) : (
                <div className="space-y-4">
                  {promoCodes.map((code) => {
                    const usagePercent = code.max_uses ? Math.round((code.current_uses / code.max_uses) * 100) : 0;
                    const isActive = code.is_active && (!code.max_uses || code.current_uses < code.max_uses);
                    return (
                      <div key={code.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Badge variant={isActive ? "default" : "secondary"}>
                              {code.code}
                            </Badge>
                            <span className="text-sm text-muted-foreground">{code.description || 'No description'}</span>
                          </div>
                          <Badge variant={isActive ? "outline" : "destructive"}>
                            {isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-4 gap-4 mt-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Redemptions</p>
                            <p className="text-lg font-bold">{code.current_uses || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Limit</p>
                            <p className="text-lg font-bold">{code.max_uses || '∞'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Type</p>
                            <p className="text-lg font-bold capitalize">{code.promo_type?.replace('_', ' ') || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Value</p>
                            <p className="text-lg font-bold">
                              {code.promo_type === 'discount' ? `${code.value}%` : 
                               code.promo_type === 'coins_bonus' ? `${code.value} coins` :
                               `${code.value} mo`}
                            </p>
                          </div>
                        </div>
                        {code.max_uses && (
                          <div className="mt-3">
                            <div className="flex justify-between text-xs text-muted-foreground mb-1">
                              <span>Usage</span>
                              <span>{usagePercent}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                style={{ width: `${Math.min(usagePercent, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats by Type */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Gift className="h-4 w-4" />
                  Free Months Given
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.redemptions_by_type?.free_months || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">via promo codes</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Coins Awarded
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.redemptions_by_type?.coins_bonus || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">bonus coin redemptions</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Percent className="h-4 w-4" />
                  Discounts Applied
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.redemptions_by_type?.discount || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">discount redemptions</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Promo Codes Section */}
      {activeSection === 'promo' && (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              Promo Codes
            </CardTitle>
            <CardDescription>Create and manage promotional codes</CardDescription>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-promo-code-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Code
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Create Promo Code</DialogTitle>
                <DialogDescription>
                  Create a new promotional code for your users
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="code">Code (optional - auto-generated if empty)</Label>
                  <Input
                    id="code"
                    placeholder="e.g., BETA2024"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    data-testid="promo-code-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Promo Type</Label>
                  <Select
                    value={newCode.promo_type}
                    onValueChange={(value) => setNewCode({ ...newCode, promo_type: value })}
                  >
                    <SelectTrigger data-testid="promo-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free_months">Free Months</SelectItem>
                      <SelectItem value="percentage_off">Percentage Off</SelectItem>
                      <SelectItem value="trial_extension">Trial Extension (days)</SelectItem>
                      <SelectItem value="lifetime_discount">Lifetime Discount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="value">
                    Value ({newCode.promo_type === 'free_months' ? 'months' : 
                            newCode.promo_type === 'trial_extension' ? 'days' : '%'})
                  </Label>
                  <Input
                    id="value"
                    type="number"
                    min="1"
                    max={newCode.promo_type.includes('percent') || newCode.promo_type === 'lifetime_discount' ? 100 : 12}
                    value={newCode.value}
                    onChange={(e) => setNewCode({ ...newCode, value: e.target.value })}
                    data-testid="promo-value-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">Internal Description</Label>
                  <Input
                    id="description"
                    placeholder="e.g., Beta tester reward"
                    value={newCode.description}
                    onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="display_message">Message Shown to User</Label>
                  <Input
                    id="display_message"
                    placeholder="e.g., Thanks for being a beta tester!"
                    value={newCode.display_message}
                    onChange={(e) => setNewCode({ ...newCode, display_message: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="max_uses">Max Uses (empty = unlimited)</Label>
                    <Input
                      id="max_uses"
                      type="number"
                      placeholder="Unlimited"
                      value={newCode.max_uses}
                      onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="expires_at">Expires On</Label>
                    <Input
                      id="expires_at"
                      type="date"
                      value={newCode.expires_at}
                      onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Tier Restriction (optional)</Label>
                  <Select
                    value={newCode.tier_restriction || "any"}
                    onValueChange={(value) => setNewCode({ ...newCode, tier_restriction: value === "any" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any tier</SelectItem>
                      <SelectItem value="recovery_plus">Recovery+ only</SelectItem>
                      <SelectItem value="recovery_elite">Recovery Elite only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="first_time_only"
                    checked={newCode.first_time_only}
                    onCheckedChange={(checked) => setNewCode({ ...newCode, first_time_only: checked })}
                  />
                  <Label htmlFor="first_time_only">First-time subscribers only</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreateCode}
                  disabled={!newCode.description || !newCode.display_message}
                  data-testid="submit-promo-code-btn"
                >
                  Create Code
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {promoCodes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No promo codes yet</p>
              <p className="text-sm">Create your first code to start promoting!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {promoCodes.map((code) => (
                <div
                  key={code.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  data-testid={`promo-code-item-${code.code}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      {getPromoTypeIcon(code.promo_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg">{code.code}</span>
                        <button
                          onClick={() => copyToClipboard(code.code)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          {copiedCode === code.code ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                        {getStatusBadge(code)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {getPromoTypeLabel(code.promo_type)} • {getValueDisplay(code)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {code.description}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm">
                        <Users className="h-4 w-4" />
                        <span>{code.current_uses}{code.max_uses ? `/${code.max_uses}` : ''}</span>
                      </div>
                      {code.expires_at && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>{new Date(code.expires_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditCode(code)}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewDetails(code)}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {code.is_active && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeactivate(code.id)}
                          className="text-destructive hover:text-destructive"
                          title="Deactivate"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              {selectedCode?.promo_code?.code}
            </DialogTitle>
            <DialogDescription>
              {selectedCode?.promo_code?.description}
            </DialogDescription>
          </DialogHeader>
          {selectedCode && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{getPromoTypeLabel(selectedCode.promo_code?.promo_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Value</p>
                  <p className="font-medium">{getValueDisplay(selectedCode.promo_code)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Uses</p>
                  <p className="font-medium">{selectedCode.promo_code?.current_uses} / {selectedCode.promo_code?.max_uses || '∞'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{new Date(selectedCode.promo_code?.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-2">Redemptions ({selectedCode.total_redemptions})</p>
                {selectedCode.redemptions?.length > 0 ? (
                  <div className="max-h-[200px] overflow-y-auto space-y-2">
                    {selectedCode.redemptions.map((redemption) => (
                      <div key={redemption.id} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                        <div>
                          <span className="font-medium">{redemption.user?.name || 'Unknown'}</span>
                          <span className="text-muted-foreground ml-2">{redemption.user?.email}</span>
                        </div>
                        <span className="text-muted-foreground">
                          {new Date(redemption.redeemed_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No redemptions yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit Promo Code: {editingCode?.code}
            </DialogTitle>
            <DialogDescription>
              Update the promo code settings. Code and type cannot be changed.
            </DialogDescription>
          </DialogHeader>
          {editingCode && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Code</Label>
                <Input value={editingCode.code} disabled className="bg-muted" />
              </div>
              <div className="grid gap-2">
                <Label>Type</Label>
                <Input value={getPromoTypeLabel(editingCode.promo_type)} disabled className="bg-muted" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-description">Internal Description</Label>
                <Input
                  id="edit-description"
                  value={editingCode.description}
                  onChange={(e) => setEditingCode({ ...editingCode, description: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-display-message">Message Shown to User</Label>
                <Input
                  id="edit-display-message"
                  value={editingCode.display_message}
                  onChange={(e) => setEditingCode({ ...editingCode, display_message: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-max-uses">Max Uses (empty = unlimited)</Label>
                  <Input
                    id="edit-max-uses"
                    type="number"
                    placeholder="Unlimited"
                    value={editingCode.max_uses}
                    onChange={(e) => setEditingCode({ ...editingCode, max_uses: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-expires-at">Expires On</Label>
                  <Input
                    id="edit-expires-at"
                    type="date"
                    value={editingCode.expires_at}
                    onChange={(e) => setEditingCode({ ...editingCode, expires_at: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-first-time-only"
                  checked={editingCode.first_time_only}
                  onCheckedChange={(checked) => setEditingCode({ ...editingCode, first_time_only: checked })}
                />
                <Label htmlFor="edit-first-time-only">First-time subscribers only</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-is-active"
                  checked={editingCode.is_active}
                  onCheckedChange={(checked) => setEditingCode({ ...editingCode, is_active: checked })}
                />
                <Label htmlFor="edit-is-active">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMarketing;
